import { assert, Constructor, createBuilder } from '@sama/js-core';
import { range, times } from '@sama/micro-dash';
import {
  Float32BufferAttribute,
  Uint16BufferAttribute,
  Uint32BufferAttribute,
  Uint8BufferAttribute,
} from 'three';
import {
  buildPositions,
  CloudAttributeOptions,
  defaultTotalPoints,
} from '../../test-helpers/builders/build-cloud-attributes';
import { CloudAttributes } from '../../utils/kd-tree/cloud-attributes.service';
import {
  cloudInstanceId,
  groundInstanceId,
} from '../../utils/kd-tree/segmentation-mask-utils';

type TypedArray =
  | Int8Array
  | Uint8Array
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Uint8ClampedArray
  | Float32Array
  | Float64Array;

export const buildPointCloudPositions = createBuilder<
  Float32BufferAttribute,
  CloudAttributeOptions
>((_, options) => {
  const groundPositions = buildPositions(
    options.minPoint,
    options.maxPoint,
    options.totalGroundPoints,
    options.groundZFunc,
  );
  const cloudPositions = buildPositions(
    options.minPoint,
    options.maxPoint,
    options.totalCloudPoints,
    options.cloudZFunc,
  );

  const groundBuffer = new Float32BufferAttribute(
    new Float32Array(groundPositions.flat()),
    3,
  );
  const cloudBuffer = new Float32BufferAttribute(
    new Float32Array(cloudPositions.flat()),
    3,
  );

  return mergePositions(groundBuffer, cloudBuffer);
});

export function buildInstanceIds(
  totalGroundPoints = defaultTotalPoints,
  totalCloudPoints = defaultTotalPoints,
) {
  const cloudPoints = new Uint32Array(totalCloudPoints).fill(cloudInstanceId);
  const groundPoints = new Uint32Array(totalGroundPoints).fill(
    groundInstanceId,
  );
  return new Uint32BufferAttribute(
    mergeTypedArrays(Uint32Array, groundPoints, cloudPoints),
    1,
  );
}

export function buildIntensities(totalPoints = defaultTotalPoints) {
  return new Uint16BufferAttribute(
    new Uint16Array(range(totalPoints).map((i) => i / totalPoints)),
    1,
  );
}

export function buildSourceIds(totalPoints = defaultTotalPoints) {
  return new Uint8BufferAttribute(
    new Uint8Array(range(totalPoints).map((i) => i % 3)),
    1,
  );
}

export function sortPoints({
  position,
  instanceId,
  intensity,
  sourceId,
}: CloudAttributes) {
  const kdPositions = position.clone();
  const kdInstanceIds = instanceId.clone();
  const kdIntensities = intensity?.clone();
  const kdSourceIds = sourceId?.clone();

  const kdSortedIndexes = computeSortedPointIndexes(
    new Uint32Array(times(position.count, (i) => i)),
    position,
  );

  kdSortedIndexes.forEach((oldIndex, newIndex) => {
    const kdPosition = getPosition(position, oldIndex);
    kdPositions.setXYZ(newIndex, kdPosition[0], kdPosition[1], kdPosition[2]);
    kdInstanceIds.setX(newIndex, instanceId.getX(oldIndex));
    kdIntensities?.setX(newIndex, intensity?.getX(oldIndex) ?? 0);
    kdSourceIds?.setX(newIndex, sourceId?.getX(oldIndex) ?? 0);
  });

  return {
    position: kdPositions,
    instanceId: kdInstanceIds,
    intensity: kdIntensities,
    sourceId: kdSourceIds,
  } as CloudAttributes;
}

function mergePositions(...positions: Float32BufferAttribute[]) {
  const itemSize = positions[0].itemSize;
  const normalized = positions[0].normalized;

  positions.forEach((p) => {
    assert(
      normalized === p.normalized,
      'normalized must be consistent across matching attributes.',
    );
  });

  const array = mergeTypedArrays(
    Float32Array,
    ...positions.map((p) => p.array as Float32Array),
  );

  return new Float32BufferAttribute(array, itemSize, normalized);
}

function mergeTypedArrays<T extends TypedArray>(
  constructor: Constructor<TypedArray>,
  ...arrays: T[]
) {
  // fix me if you change this code
  // eslint-disable-next-line no-param-reassign -- fix me!
  const arrayLength = arrays.reduce((acc, array) => (acc += array.length), 0);
  const merged = new constructor(arrayLength);
  let offset = 0;

  arrays.forEach((a) => {
    merged.set(a, offset);
    offset += a.length;
  });
  return merged;
}

/*
 * Sorts the indexes array to KD tree format that the spc array should be in
 *
 * Note: if there are multiple points matching the median value the ordering
 * will not be deterministic
 */
function computeSortedPointIndexes(
  indexes: Uint32Array,
  spc: Float32BufferAttribute,
) {
  const sortedIndexes: number[] = [];
  let level = [indexes];
  let axis = 0;
  while (level.length) {
    const nextLevel: Uint32Array[] = [];
    for (const slice of level) {
      if (slice.length === 0) {
        continue;
      }

      slice.sort(
        (a, b) => getPosition(spc, a)[axis] - getPosition(spc, b)[axis],
      );
      const median = Math.floor(slice.length / 2);
      sortedIndexes.push(slice[median]);

      nextLevel.push(slice.subarray(0, median));
      nextLevel.push(slice.subarray(median + 1));
    }

    level = nextLevel;
    axis = (axis + 1) % 3;
  }
  return sortedIndexes;
}

function getPosition(positions: Float32BufferAttribute, i: number) {
  return [positions.getX(i), positions.getY(i), positions.getZ(i)];
}
